import React, { useState } from 'react';
import { CheckCircle, MapPin, Thermometer, User, ChevronDown, ChevronUp } from 'lucide-react';

const BatchTimeline = ({ events }) => {
  const [expandedEvent, setExpandedEvent] = useState(null);

  const eventIcons = {
    catch: '🎣',
    processing: '⚙️',
    quality_check: '✔️',
    storage: '❄️',
    transport: '🚚',
    delivery: '🎯',
  };

  const eventColors = {
    catch: 'border-blue-300 bg-blue-50',
    processing: 'border-amber-300 bg-amber-50',
    quality_check: 'border-emerald-300 bg-emerald-50',
    storage: 'border-indigo-300 bg-indigo-50',
    transport: 'border-pink-300 bg-pink-50',
    delivery: 'border-teal-300 bg-teal-50',
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleEvent = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  return (
    <div className="relative" data-testid="batch-timeline">
      {/* Vertical Line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>

      {/* Events */}
      <div className="space-y-6">
        {events.map((event, idx) => {
          const isExpanded = expandedEvent === event.id;
          const isLast = idx === events.length - 1;

          return (
            <div key={event.id} className="relative" data-testid={`timeline-event-${event.event_type}`}>
              {/* Timeline Dot */}
              <div className="absolute left-3 top-0 transform -translate-x-1/2">
                <div className={`w-6 h-6 rounded-full border-4 ${eventColors[event.event_type]} flex items-center justify-center z-10`}>
                  {event.verified && <CheckCircle className="w-3 h-3 text-emerald-600" />}
                </div>
              </div>

              {/* Event Card */}
              <div className="ml-12">
                <div
                  onClick={() => toggleEvent(event.id)}
                  className={`bg-white rounded-lg border-2 ${eventColors[event.event_type]} p-4 hover:shadow-md transition-all cursor-pointer`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{eventIcons[event.event_type]}</span>
                        <h3 className="text-base font-semibold text-slate-900 capitalize">
                          {event.event_type.replace('_', ' ')}
                        </h3>
                        {event.verified && (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                            Verified
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{event.description}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span>{event.location.name}</span>
                        </div>
                        <span>•</span>
                        <span>{formatDate(event.timestamp)}</span>
                      </div>
                    </div>
                    <button className="text-slate-400 hover:text-slate-600">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Location Details</p>
                          <p className="text-sm font-medium text-slate-900">{event.location.name}</p>
                          <p className="text-xs text-slate-600">{event.location.region}</p>
                          <p className="text-xs font-mono text-slate-500 mt-1">
                            {event.location.coordinates.lat.toFixed(2)}°N, {event.location.coordinates.lng.toFixed(2)}°E
                          </p>
                        </div>
                        {event.temperature && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1">
                              <Thermometer className="w-3 h-3 inline mr-1" />
                              Temperature
                            </p>
                            <p className="text-sm font-medium text-slate-900">{event.temperature}°C</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">
                          <User className="w-3 h-3 inline mr-1" />
                          Operator
                        </p>
                        <p className="text-sm font-medium text-slate-900">{event.operator}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BatchTimeline;
